/**
 * Image authoring checks (R23) — the loader's half of the D89 contract.
 *
 * @remarks
 * An image is a promise made without words, so the contract makes the promise
 * DECLARED and this module makes the declaration checked. Three families of
 * rule, all collected (never first-failure), all `reference_broken` shaped:
 *
 * - **Resolution, both directions.** Every plate a room names (plain,
 *   fallback, or variant) must exist in the pack's `images` table — and every
 *   table entry must be named by SOME room, because a plate nothing shows is
 *   authored content no play can reach (the D31/D47 dead-content law,
 *   image-side).
 * - **`depicts` against the rooms that use the plate.** Checked PER ROOM-USE,
 *   not per table row: a corridor plate serving two corridors must show only
 *   what is referable in EACH of them — a depicted item must be placed in
 *   that room or ambient to the pack. This cannot prove the painting matches
 *   the list (nothing short of a human eye can); it proves the list matches
 *   the world, which converts an invisible promise into a reviewable one.
 * - **Declaration hygiene.** Duplicate image ids, duplicate `depicts`
 *   entries, and `depicts` naming an undeclared item are each their own loud
 *   failure, because every downstream check reasons from these declarations.
 *
 * Variant CONDITIONS are deliberately not here: a conditional plate reads the
 * world exactly as conditional text does, so its conditions join the one walk
 * in `semantic-validation.ts` — and the constant-condition reachability check
 * finds conditional-image nodes structurally (`fallback` + `variants`), so a
 * variant guarded by an impossible condition is caught with no new code.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, RoomDefinition } from '@orpheus/content-schema';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference, collectDuplicates } from './semantic-validation.js';

/**
 * Every plate reference one room makes, with the path that made it.
 *
 * @param room - The room
 * @param roomIndex - Its index (paths cite it)
 * @returns The referenced image ids, in authored order
 */
export const imageReferencesOf = (
	room: RoomDefinition,
	roomIndex: number,
): readonly { readonly imageId: string; readonly referencePath: string }[] => {
	const imagePath = `rooms[${String(roomIndex)}].image`;

	if (room.image === undefined) {
		return [];
	}

	if (typeof room.image === 'string') {
		return [{ imageId: room.image, referencePath: imagePath }];
	}

	return [
		{ imageId: room.image.fallback, referencePath: `${imagePath}.fallback` },
		...room.image.variants.map((variant, variantIndex) => ({
			imageId: variant.imageId,
			referencePath: `${imagePath}.variants[${String(variantIndex)}].imageId`,
		})),
	];
};

/**
 * Run every R23 image check against a schema-valid pack.
 *
 * @param contentPack - The parsed, schema-valid pack
 * @returns Every image-authoring failure found (empty = the declarations hold)
 */
export const validateImageAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	// Called for the failure it RECORDS, not for the set it returns: a duplicate
	// image id makes every reference to that id ambiguous. The returned set is
	// no longer needed — the `depictsByImageId` lookup below answers "does the
	// table declare this?" and "what does it show?" in one read.
	collectDuplicates(
		contentPack.images.map((image, imageIndex) => ({
			declarationPath: `images[${String(imageIndex)}].imageId`,
			declaredId: image.imageId,
		})),
		'image',
		failures,
	);
	const declaredItemIds = new Set<string>(contentPack.items.map((item) => item.itemId));
	const ambientItemIds = new Set<string>(contentPack.ambientItemIds);
	const depictsByImageId = new Map(contentPack.images.map((image) => [image.imageId as string, image.depicts]));
	const usedImageIds = new Set<string>();

	// Declaration hygiene: a depicts list is the checked half of the promise,
	// so an undeclared or repeated entry is a defect in the declaration itself.
	contentPack.images.forEach((image, imageIndex) => {
		const seenDepictedIds = new Set<string>();

		image.depicts.forEach((depictedItemId, depictedIndex) => {
			const depictsPath = `images[${String(imageIndex)}].depicts[${String(depictedIndex)}]`;

			if (!declaredItemIds.has(depictedItemId)) {
				failures.push(brokenReference(depictsPath, depictedItemId, 'the plate declares it depicts an undeclared item'));
			}
			if (seenDepictedIds.has(depictedItemId)) {
				failures.push(brokenReference(depictsPath, depictedItemId, 'duplicate depicts entry — the declaration lists this item twice'));
			}
			seenDepictedIds.add(depictedItemId);
		});
	});

	contentPack.rooms.forEach((room, roomIndex) => {
		const placedItemIds = new Set<string>(room.itemPlacements);

		for (const { imageId, referencePath } of imageReferencesOf(room, roomIndex)) {
			usedImageIds.add(imageId);

			// One lookup answers both questions — whether the table declares this
			// plate, and what it says the plate shows. Two parallel maps made the
			// second answer optional when it never is, leaving a fallback nothing
			// could reach (§2.1: nothing shipped that nothing can reach).
			const depicts = depictsByImageId.get(imageId);

			if (depicts === undefined) {
				failures.push(brokenReference(referencePath, imageId, 'the room names a plate the images table does not declare'));
				continue;
			}

			// The D89 promise check, per room-use: everything this plate shows
			// must be referable where it is shown.
			for (const depictedItemId of depicts) {
				if (declaredItemIds.has(depictedItemId) && !placedItemIds.has(depictedItemId) && !ambientItemIds.has(depictedItemId)) {
					failures.push(
						brokenReference(
							referencePath,
							depictedItemId,
							`the plate "${imageId}" depicts "${depictedItemId}", which is neither placed in this room nor ambient — an image must not promise what the room cannot answer`,
						),
					);
				}
			}
		}
	});

	// The dead-content law, image-side: a declared plate no room ever names.
	contentPack.images.forEach((image, imageIndex) => {
		if (!usedImageIds.has(image.imageId)) {
			failures.push(
				brokenReference(
					`images[${String(imageIndex)}].imageId`,
					image.imageId,
					'no room names this plate, so it can never be shown',
				),
			);
		}
	});

	return failures;
};
